/**
 * Sticky Add to Cart Button Component
 *
 * Makes the .product-form-buttons element fixed at the bottom of the viewport
 * when it scrolls out of view. Mobile only (< 750px).
 */
class BuyButtonsSticky extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLElement | null} */
  #buttonsElement = null;

  /** @type {HTMLElement | null} */
  #sentinel = null;

  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  connectedCallback() {
    if (!this.hasAttribute('data-sticky-enabled')) return;

    this.#buttonsElement = this.querySelector('.product-form-buttons--can-sticky');

    if (!this.#buttonsElement) return;

    this.#mobileQuery = window.matchMedia('(max-width: 749px)');
    this.#createSentinel();

    // Only init on mobile
    if (this.#mobileQuery.matches) {
      this.#initObserver();
    }

    // Listen for screen size changes
    this.#mobileQuery.addEventListener('change', this.#handleMediaChange.bind(this));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#sentinel?.remove();
    this.#mobileQuery?.removeEventListener('change', this.#handleMediaChange.bind(this));
  }

  /** @param {MediaQueryListEvent} event */
  #handleMediaChange(event) {
    if (event.matches) {
      // Switched to mobile - enable sticky
      this.#initObserver();
    } else {
      // Switched to desktop - disable sticky
      this.#observer?.disconnect();
      this.#observer = null;
      this.#buttonsElement?.classList.remove('is-sticky');
    }
  }

  /**
   * Create a sentinel element to observe instead of the buttons
   * This prevents the infinite loop caused by position: fixed
   */
  #createSentinel() {
    this.#sentinel = document.createElement('div');
    this.#sentinel.className = 'buy-buttons-sticky-sentinel';
    this.#sentinel.style.cssText = 'height: 1px; width: 100%; pointer-events: none;';
    this.#buttonsElement?.parentNode?.insertBefore(this.#sentinel, this.#buttonsElement);
  }

  #initObserver() {
    if (!this.#sentinel) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    };

    this.#observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.#buttonsElement?.classList.remove('is-sticky');
        } else {
          this.#buttonsElement?.classList.add('is-sticky');
        }
      });
    }, options);

    this.#observer.observe(this.#sentinel);
  }
}

if (!customElements.get('buy-buttons-sticky')) {
  customElements.define('buy-buttons-sticky', BuyButtonsSticky);
}
